import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import ConfettiEffect from '../components/ConfettiEffect';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { saveUserEntry } from '../utils/db';
import { 
  playCardFlipSound, 
  playAnimalCheerSound, 
  playCorrectChime, 
  playWinFanfare, 
  playTickSound 
} from '../utils/soundEffects';
import { useGameExitRoute } from '../utils/navigation';

const { FiAward, FiSun, FiMoon, FiHome, FiRotateCcw, FiStar, FiHeart, FiSmile, FiUser, FiArrowRight } = FiIcons;

const SAFARI_ANIMALS = [
  { id: 'lion', emoji: '🦁', name: 'Sheru Lion', sound: 'Roar!', bg: 'from-amber-400 to-orange-500' },
  { id: 'elephant', emoji: '🐘', name: 'Gajaraj Elephant', sound: 'Trumpet!', bg: 'from-blue-400 to-indigo-500' },
  { id: 'peacock', emoji: '🦚', name: 'Mayur Peacock', sound: 'Dance!', bg: 'from-emerald-400 to-teal-500' },
  { id: 'tiger', emoji: '🐯', name: 'Bagheera Tiger', sound: 'Stripes!', bg: 'from-orange-400 to-red-500' },
  { id: 'monkey', emoji: '🐵', name: 'Bandar Monkey', sound: 'Chatter!', bg: 'from-yellow-400 to-amber-600' },
  { id: 'rabbit', emoji: '🐰', name: 'Khargosh Rabbit', sound: 'Hop Hop!', bg: 'from-pink-400 to-rose-500' }
];

const JUNIOR_REWARDS = [
  "🦁 Brave Little Lion Star Badge",
  "🌟 Junior Safari Explorer Ribbon",
  "🖍️ Cartoon Crayons Color Pack",
  "🍫 Kinder Choco Treat",
  "🎨 Animal Sticker Fun Sheet",
  "⭐ Golden Shining Star Pin"
];

const AnimalMatchFlow = () => {
  const { exitRoute, exitTitle } = useGameExitRoute();
  const [step, setStep] = useState('welcome'); // 'welcome' | 'play' | 'won'
  const [kidName, setKidName] = useState('');
  const [cards, setCards] = useState([]);
  const [flippedIndices, setFlippedIndices] = useState([]);
  const [matchedIds, setMatchedIds] = useState([]);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [wonPrize, setWonPrize] = useState('');
  const [couponCode, setCouponCode] = useState('');

  // Initialize and shuffle 6 pairs (12 cards)
  const setupGame = () => {
    const deck = [];
    SAFARI_ANIMALS.forEach((animal) => {
      deck.push({ ...animal, uniqueKey: `${animal.id}-1` });
      deck.push({ ...animal, uniqueKey: `${animal.id}-2` });
    });
    // Shuffle
    const shuffled = deck.sort(() => Math.random() - 0.5);
    setCards(shuffled);
    setFlippedIndices([]);
    setMatchedIds([]);
  };

  const handleStartGame = (e) => {
    e.preventDefault();
    setupGame();
    setStep('play');
    playTickSound(700, 0.06);
  };

  const handleCardClick = (index) => {
    // If already flipped 2 cards or clicked card is already flipped/matched
    if (flippedIndices.length === 2) return;
    if (flippedIndices.includes(index)) return;
    if (matchedIds.includes(cards[index].id)) return;

    playCardFlipSound();
    const newFlipped = [...flippedIndices, index];
    setFlippedIndices(newFlipped);

    if (newFlipped.length === 2) {
      const firstCard = cards[newFlipped[0]];
      const secondCard = cards[newFlipped[1]];

      if (firstCard.id === secondCard.id) {
        // Match!
        playAnimalCheerSound();
        const nextMatched = [...matchedIds, firstCard.id];
        setMatchedIds(nextMatched);
        setFlippedIndices([]);

        // Check if all 6 animals are matched!
        if (nextMatched.length === SAFARI_ANIMALS.length) {
          setTimeout(() => {
            const prize = JUNIOR_REWARDS[Math.floor(Math.random() * JUNIOR_REWARDS.length)];
            const code = 'SAFARI-' + Math.random().toString(36).substring(2, 7).toUpperCase();
            setWonPrize(prize);
            setCouponCode(code);

            saveUserEntry({
              name: kidName.trim() || 'Little Explorer',
              mobile: 'Junior (Ages 3-7)',
              classGrade: 'Pre-Primary / KG',
              reward: prize,
              couponCode: code,
              gameType: 'Animal Safari Match'
            });

            setStep('won');
            playWinFanfare();
          }, 800);
        }
      } else {
        // No match, flip back gently after 1 second
        setTimeout(() => {
          setFlippedIndices([]);
        }, 900);
      }
    }
  };

  const handlePlayAgain = () => {
    setKidName('');
    setWonPrize('');
    setCouponCode('');
    setStep('welcome');
  };

  return (
    <div className={`min-h-screen transition-colors duration-500 flex flex-col justify-between p-4 md:p-6 select-none ${
      isDarkMode 
        ? 'bg-slate-950 text-slate-100' 
        : 'bg-gradient-to-br from-amber-50 via-emerald-50 to-sky-50 text-slate-900'
    }`}>
      {step === 'won' && <ConfettiEffect />}

      {/* Top Header */}
      <header className="flex items-center justify-between pb-3 max-w-4xl mx-auto w-full border-b border-amber-200 dark:border-slate-800">
        <div className="flex items-center gap-3">
          <Link to={exitRoute} title={exitTitle} className={`p-2.5 rounded-full border transition-all ${
            isDarkMode ? 'bg-slate-900 border-slate-700 text-slate-300' : 'bg-white border-amber-200 text-slate-700 shadow-sm'
          }`}>
            <SafeIcon icon={FiHome} className="text-xl" />
          </Link>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-2xl">🦁</span>
              <h1 className="text-xl md:text-2xl font-black bg-clip-text text-transparent bg-gradient-to-r from-amber-600 to-emerald-600">
                Jungle Safari Animal Match
              </h1>
            </div>
            <p className="text-[11px] font-bold text-amber-600 dark:text-amber-400 uppercase tracking-wider">
              Specially for Ages 3-7 Years • Tap & Find Animal Friends!
            </p>
          </div>
        </div>

        {/* Day / Night Toggle */}
        <button 
          onClick={() => setIsDarkMode(!isDarkMode)}
          className={`px-3 py-1.5 rounded-full text-xs font-bold border flex items-center gap-1.5 transition-all cursor-pointer ${
            isDarkMode ? 'bg-amber-400 text-slate-950 border-amber-300 shadow-md' : 'bg-white text-slate-800 border-amber-200 shadow-sm'
          }`}
        >
          <SafeIcon icon={isDarkMode ? FiSun : FiMoon} />
          <span>{isDarkMode ? 'Day ☀️' : 'Night 🌙'}</span>
        </button>
      </header>

      {/* Main Container */}
      <main className="flex-1 flex flex-col items-center justify-center my-4 max-w-3xl mx-auto w-full">
        <AnimatePresence mode="wait">
          {step === 'welcome' && (
            <motion.div
              key="welcome"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className={`w-full max-w-md p-8 rounded-3xl border text-center shadow-xl ${
                isDarkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-amber-200 shadow-amber-200/40'
              }`}
            >
              <div className="flex justify-center gap-2 text-5xl mb-4 animate-bounce">
                <span>🦁</span>
                <span>🐘</span>
                <span>🦚</span>
                <span>🐰</span>
              </div>

              <h2 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white mb-2">
                Hello Little Friend!
              </h2>
              <p className="text-sm text-slate-500 mb-6">
                Can you find all the matching animal friends in the Jungle Safari?
              </p>

              <form onSubmit={handleStartGame} className="space-y-4">
                <div className="text-left">
                  <label className="block text-xs font-black uppercase tracking-wider text-slate-500 mb-1.5 flex items-center gap-1.5">
                    <SafeIcon icon={FiUser} className="text-amber-500" />
                    <span>Little Champ's Name (Optional)</span>
                  </label>
                  <input
                    type="text"
                    value={kidName}
                    onChange={e => setKidName(e.target.value)}
                    placeholder="e.g. Ananya / Kabir"
                    className="w-full px-4 py-3.5 rounded-2xl border text-base font-bold focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full py-4 rounded-2xl bg-gradient-to-r from-amber-500 via-orange-500 to-emerald-500 text-white font-black text-lg shadow-xl shadow-amber-500/30 flex items-center justify-center gap-2 hover:scale-[1.02] active:scale-[0.98] transition-all cursor-pointer"
                >
                  <span>Start Safari Game!</span>
                  <SafeIcon icon={FiArrowRight} />
                </button>
              </form>
            </motion.div>
          )}

          {step === 'play' && (
            <motion.div
              key="play"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="w-full flex flex-col items-center"
            >
              {/* Progress Tracker Stars */}
              <div className="flex items-center gap-3 mb-5 px-5 py-2 rounded-full bg-white/80 dark:bg-slate-900/80 border border-amber-300 dark:border-slate-700 shadow-sm">
                <span className="text-xs font-black uppercase text-amber-600 dark:text-amber-400">
                  {kidName || 'Little Champ'}'s Safari Stars:
                </span>
                <div className="flex gap-1">
                  {Array.from({ length: SAFARI_ANIMALS.length }).map((_, i) => (
                    <span 
                      key={i} 
                      className={`text-lg transition-transform ${
                        i < matchedIds.length ? 'text-amber-400 scale-125' : 'text-slate-300 dark:text-slate-700'
                      }`}
                    >
                      ⭐
                    </span>
                  ))}
                </div>
                <span className="text-xs font-bold text-slate-500 ml-1">
                  {matchedIds.length}/{SAFARI_ANIMALS.length}
                </span>
              </div>

              {/* 3x4 Big Chunky Animal Cards */}
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 sm:gap-4 max-w-xl w-full">
                {cards.map((card, idx) => {
                  const isFlipped = flippedIndices.includes(idx) || matchedIds.includes(card.id);
                  const isMatched = matchedIds.includes(card.id);

                  return (
                    <motion.div
                      key={card.uniqueKey}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => handleCardClick(idx)}
                      className={`h-24 sm:h-28 rounded-2xl flex flex-col items-center justify-center cursor-pointer transition-all duration-300 relative shadow-md ${
                        isMatched
                          ? 'bg-gradient-to-br from-emerald-400 to-teal-500 text-white border-2 border-emerald-300 opacity-90'
                          : isFlipped
                          ? `bg-gradient-to-br ${card.bg} text-white border-2 border-white shadow-lg`
                          : isDarkMode
                          ? 'bg-slate-800 hover:bg-slate-700 border-2 border-slate-700 text-amber-400'
                          : 'bg-white hover:bg-amber-50 border-2 border-amber-200 text-amber-500'
                      }`}
                    >
                      {isFlipped ? (
                        <motion.div 
                          initial={{ scale: 0, rotate: -20 }}
                          animate={{ scale: 1, rotate: 0 }}
                          className="flex flex-col items-center"
                        >
                          <span className="text-4xl sm:text-5xl drop-shadow-md">{card.emoji}</span>
                          <span className="text-[10px] font-black uppercase tracking-wider mt-1 text-white">
                            {card.name.split(' ')[0]}
                          </span>
                        </motion.div>
                      ) : (
                        <div className="flex flex-col items-center">
                          <span className="text-3xl opacity-80">🐾</span>
                          <span className="text-[9px] font-black uppercase tracking-widest mt-1 opacity-60">
                            Tap Me
                          </span>
                        </div>
                      )}
                    </motion.div>
                  );
                })}
              </div>

              <div className="mt-5 text-center text-xs font-bold text-slate-400">
                Tap two cards with the same animal to make a pair! 🐾
              </div>
            </motion.div>
          )}

          {step === 'won' && (
            <motion.div
              key="won"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              className={`w-full max-w-md p-8 rounded-3xl border text-center shadow-2xl ${
                isDarkMode ? 'bg-slate-900 border-amber-500/40' : 'bg-white border-amber-200 shadow-amber-200/50'
              }`}
            >
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-amber-400 to-emerald-500 text-white flex items-center justify-center text-4xl mx-auto mb-3 shadow-xl">
                <SafeIcon icon={FiAward} />
              </div>

              <div className="text-xs font-black uppercase text-amber-600 tracking-widest mb-1">
                🌟 Super Safari Explorer!
              </div>

              <h2 className="text-3xl font-black text-slate-900 dark:text-white mb-2">
                Yay, {kidName || 'Little Champ'}!
              </h2>

              <p className="text-xs text-slate-500 mb-6">
                You found all the animal friends in the Jungle Safari!
              </p>

              <div className="p-5 rounded-2xl bg-amber-50 dark:bg-amber-950/40 border-2 border-amber-300 dark:border-amber-700/50 mb-6">
                <span className="text-xs text-amber-800 dark:text-amber-300 font-black uppercase tracking-wider block mb-1">
                  🎁 Safari Reward
                </span>
                <span className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white">
                  {wonPrize}
                </span>
              </div>

              <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded-2xl inline-block mb-6 border border-slate-300 dark:border-slate-700">
                <p className="text-[10px] text-slate-400 uppercase font-black tracking-widest">
                  Kid's Gift Pass Code
                </p>
                <p className="text-lg font-mono font-black text-emerald-600 dark:text-emerald-400">
                  {couponCode}
                </p>
              </div>

              <button
                onClick={handlePlayAgain}
                className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-amber-500 to-emerald-600 text-white font-black text-base shadow-lg shadow-amber-500/30 hover:scale-[1.02] active:scale-[0.98] transition-all cursor-pointer flex items-center justify-center gap-2"
              >
                <SafeIcon icon={FiRotateCcw} />
                <span>Play Safari Again!</span>
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Footer */}
      <footer className="text-center text-xs text-slate-400 py-2 border-t border-amber-100 dark:border-slate-800">
        GamifyCenter Junior • Created with Care for Early Learners (Ages 3-7)
      </footer>
    </div>
  );
};

export default AnimalMatchFlow;
